import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import axiosInstance from "../../apis/axiosConfig";
import { startSeoCheckApi } from "../../apis/projects.api";
import { scanSiteEndpointsApi } from "../../apis/seo.api";

// Optional future backend integration example
// import { scanSiteEndpointsApi } from "../apis/seo.api";

// const commonEndpoints = [
//   "/about",
//   "/about-us",
//   "/contact",
//   "/contact-us",
//   "/services",
//   "/service",
//   "/pricing",
//   "/plans",
//   "/blog",
//   "/news",
//   "/faq",
//   "/faqs",
//   "/careers",
//   "/jobs",
//   "/portfolio",
//   "/projects",
//   "/terms",
//   "/privacy",
// ];

const EndpointScanner = () => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    prefilledUrl,
    projectName,
    projectId,
    keyword: projectKeyword,
  } = location.state || {};

  const [baseUrl, setBaseUrl] = useState(prefilledUrl || "");
  const [keyword, setKeyword] = useState(projectKeyword || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [selected, setSelected] = useState({});
  const [advanced, setAdvanced] = useState(true);
  const [includeDeepPaths, setIncludeDeepPaths] = useState(true);

  const origin = useMemo(() => {
    try {
      if (!baseUrl) return "";
      const u = new URL(baseUrl);
      return `${u.protocol}//${u.host}`;
    } catch {
      return "";
    }
  }, [baseUrl]);

  const toggleAll = (checked) => {
    const map = {};
    for (const ep of endpoints) map[ep] = checked;
    setSelected(map);
  };

  const handleToggle = (path) => {
    setSelected((s) => ({ ...s, [path]: !s[path] }));
  };

  const parseSitemapXml = (xmlText) => {
    try {
      const parser = new DOMParser();
      const xml = parser.parseFromString(xmlText, "application/xml");
      const locs = Array.from(xml.getElementsByTagName("loc"))
        .map((n) => n.textContent?.trim())
        .filter(Boolean);
      const urlObjs = [];
      for (const loc of locs) {
        try {
          urlObjs.push(new URL(loc));
        } catch {}
      }
      return urlObjs;
    } catch (e) {
      return [];
    }
  };

  const uniquePathsFromUrls = (urlObjs) => {
    const set = new Set();
    const looksLikePage = (path) => {
      if (!path) return false;
      const blockedPrefixes = [
        "/wp-content/",
        "/wp-includes/",
        "/wp-admin/",
        "/wp-json/",
        "/static/",
        "/media/",
        "/assets/",
        "/images/",
        "/img/",
        "/files/",
        "/fonts/",
      ];
      for (const pref of blockedPrefixes) {
        if (path.startsWith(pref)) return false;
      }
      if (path === "/" || path.endsWith("/")) return true;
      const last = path.split("/").pop() || "";
      if (!last.includes(".")) return true;
      const ext = last.split(".").pop().toLowerCase();
      const blockedExts = new Set([
        "png",
        "jpg",
        "jpeg",
        "gif",
        "svg",
        "png",
        "ico",
        "bmp",
        "tiff",
        "avif",
        "heic",
        "heif",
        "mp3",
        "wav",
        "ogg",
        "mp4",
        "webm",
        "mov",
        "avi",
        "mkv",
        "flv",
        "m4a",
        "m4v",
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "ppt",
        "pptx",
        "zip",
        "rar",
        "7z",
        "tar",
        "gz",
        "bz2",
        "css",
        "js",
        "map",
        "json",
        "woff",
        "woff2",
        "ttf",
        "otf",
        "eot",
        "xml",
        "rss",
        "atom",
      ]);
      if (blockedExts.has(ext)) return false;
      const allowedPageExts = new Set(["html", "htm", "php", "aspx", "jsp"]);
      return allowedPageExts.has(ext);
    };
    for (const u of urlObjs) {
      // Only include same host
      if (origin && `${u.protocol}//${u.host}` !== origin) continue;
      if (!includeDeepPaths) {
        const seg = u.pathname.split("/").filter(Boolean)[0];
        const p = seg ? `/${seg}` : "/";
        if (looksLikePage(p)) set.add(p);
      } else {
        const p = u.pathname.endsWith("/")
          ? u.pathname.slice(0, -1)
          : u.pathname;
        const pp = p || "/";
        if (looksLikePage(pp)) set.add(pp);
      }
    }
    return Array.from(set).sort();
  };

  const scanViaBackend = async () => {
    setLoading(true);
    setError(null);
    setEndpoints([]);
    setSelected({});

    try {
      if (!origin)
        throw new Error(
          "Please enter a valid website URL (including http/https)",
        );
      const resp = await scanSiteEndpointsApi({
        url: baseUrl,
        include_deep_paths: includeDeepPaths,
        validate_urls: true,
      });
      const eps = resp?.data?.endpoints || [];
      if (!eps.length) throw new Error("No endpoints discovered");
      setEndpoints(eps);
      const map = {};
      for (const p of eps) map[p] = true;
      setSelected(map);
    } catch (e) {
      setError(
        e?.response?.data?.error || e.message || "Server-side discovery failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const scanViaSitemap = async () => {
    setLoading(true);
    setError(null);
    setEndpoints([]);
    setSelected({});

    try {
      if (!origin)
        throw new Error(
          "Please enter a valid website URL (including http/https)",
        );
      // Always use server-side discovery to avoid browser CORS on robots/sitemaps
      const resp = await scanSiteEndpointsApi({
        url: baseUrl,
        include_deep_paths: includeDeepPaths,
        validate_urls: true,
      });
      const eps = resp?.data?.endpoints || [];
      if (!eps.length) throw new Error("No endpoints discovered");
      setEndpoints(eps);
      const map = {};
      for (const p of eps) map[p] = true;
      setSelected(map);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  // const quickScan = () => {
  //   setError(null);
  //   try {
  //     if (!origin) throw new Error("Please enter a valid website URL (including http/https)");
  //     const guessed = Array.from(new Set(["/", ...commonEndpoints])).sort();
  //     setEndpoints(guessed);
  //     const map = {};
  //     for (const p of guessed) map[p] = true;
  //     setSelected(map);
  //   } catch (e) {
  //     setError(e.message || "Invalid URL");
  //   }
  // };

  const startSeoScan = async () => {
    const selectedEndpoints = endpoints.filter((p) => selected[p]);

    if (!projectId) {
      setError(
        "No project context. Please open the endpoint scanner from a project card.",
      );
      return;
    }

    if (selectedEndpoints.length === 0) {
      alert("Please select at least one endpoint to scan.");
      return;
    }

    const confirmed = window.confirm(
      `Run SEO analysis for ${projectName || "this project"} on ${selectedEndpoints.length} endpoint(s)?\n\nNo new project will be created; results will be shown for the existing project.`,
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setError(null);

      // Optionally update keyword for this project before scanning
      if (keyword) {
        try {
          await axiosInstance.put(`/projects/edit/${projectId}/`, { keyword });
        } catch (e) {
          // console.warn('Keyword update failed, continuing scan', e);
        }
      }

      // Start SEO check for existing project (no new project creation)
      await startSeoCheckApi(projectId);

      // Navigate to Selected Endpoints page where user can view per-endpoint results
      navigate("/dashboard/seo-endpoints", {
        state: {
          projectId,
          projectName: projectName || origin.replace(/^https?:\/\//, ""),
          url: origin,
          keyword: keyword || projectKeyword || "",
          endpoints: selectedEndpoints,
        },
      });
    } catch (err) {
      // console.error('Error starting SEO scan:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to start SEO scan",
      );
    } finally {
      setLoading(false);
    }
  };

  const copySelected = async () => {
    const sel = endpoints.filter((p) => selected[p]);
    const urls = sel.map((p) => `${origin}${p}`);
    try {
      await navigator.clipboard.writeText(urls.join("\n"));
      alert("Copied selected endpoints to clipboard.");
    } catch {
      alert("Could not copy to clipboard.");
    }
  };

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div
      className={`min-h-screen py-8 px-4 ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}
    >
      <div className="max-w-5xl mx-auto">
        <div
          className={`rounded-xl shadow-lg p-6 mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1
                className={`text-2xl md:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}
              >
                Endpoint Scanner
              </h1>
              {projectName && (
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  Project: <span className="font-semibold">{projectName}</span>
                </p>
              )}
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="bg-[#008300] text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
            >
              ← Back to Dashboard
            </button>
          </div>

          <p className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            <strong>Step 1:</strong> Use the project URL (read-only) and
            discover its endpoints (pages like /services, /contact, /about).
            <br />
            <strong>Step 2:</strong> Select which endpoints you want to analyze.
            <br />
            <strong>Step 3:</strong> Run SEO analysis on your selected endpoints
            without creating new projects.
          </p>

          {!projectId && (
            <div
              className={`mt-3 p-3 rounded border text-sm ${isDarkMode ? "bg-red-900/30 border-red-800 text-red-100" : "bg-red-50 border-red-200 text-red-700"}`}
            >
              Open this page from a project card (Find Site Endpoints) to use a
              project's URL.
            </div>
          )}

          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto]">
            <input
              value={baseUrl}
              readOnly
              placeholder="Project URL"
              className={`w-full px-3 py-2 rounded border focus:outline-none opacity-60 cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-700 text-white" : "bg-gray-100 border-gray-300 text-gray-700"} ${!projectId ? "opacity-70 cursor-not-allowed" : ""}`}
            />
            <div className="flex gap-2">
              <button
                onClick={scanViaSitemap}
                disabled={loading || !projectId}
                className={`px-4 py-2 rounded-lg text-white ${loading ? "opacity-60" : ""} ${isDarkMode ? "bg-[#008300] hover:bg-green-500" : "bg-[#008300] hover:bg-green-700"}`}
              >
                {loading ? "🔍 Discovering..." : "🔍 Find Endpoints (Server)"}
              </button>
              {/* <button
                onClick={quickScan}
                disabled={loading || !projectId}
                className={`px-4 py-2 rounded-lg text-white ${loading ? "opacity-60" : ""} ${isDarkMode ? "bg-purple-700 hover:bg-purple-800" : "bg-purple-600 hover:bg-purple-700"}`}
              >
                ⚡ Quick Find (Common)
              </button> */}
            </div>
          </div>

          {/* Keyword input (optional update before scan) */}
          <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto] items-center">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Keyword for SEO scan (optional)"
              className={`w-full px-3 py-2 rounded border ${isDarkMode ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}
              disabled={!projectId}
            />
            <span
              className={`${isDarkMode ? "text-gray-400" : "text-gray-600"} text-sm`}
            >
              Will update project keyword before scan
            </span>
          </div>

          <div className="mt-3 flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={advanced}
                onChange={(e) => setAdvanced(e.target.checked)}
              />
              <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                Show advanced options
              </span>
            </label>
            {advanced && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeDeepPaths}
                  onChange={(e) => setIncludeDeepPaths(e.target.checked)}
                />
                <span
                  className={isDarkMode ? "text-gray-300" : "text-gray-600"}
                >
                  Include deeper paths (recommended)
                </span>
              </label>
            )}
          </div>

          {error && (
            <div
              className={`mt-4 p-3 rounded ${isDarkMode ? "bg-red-900/40 text-red-200" : "bg-red-50 text-red-700"}`}
            >
              {error}
            </div>
          )}

          {loading && (
            <div
              className={`mt-6 p-4 rounded ${isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-50 text-gray-700"}`}
            >
              {endpoints.length === 0
                ? "Discovering endpoints... Please wait."
                : "Processing... Please wait."}
            </div>
          )}
        </div>

        {/* Results */}
        {endpoints.length > 0 && (
          <div
            className={`rounded-xl shadow-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-800"}`}
              >
                Found {endpoints.length} endpoint
                {endpoints.length > 1 ? "s" : ""}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleAll(true)}
                  className={`px-3 py-1 rounded border ${isDarkMode ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 hover:bg-gray-50"}`}
                >
                  Select All
                </button>
                <button
                  onClick={() => toggleAll(false)}
                  className={`px-3 py-1 rounded border ${isDarkMode ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 hover:bg-gray-50"}`}
                >
                  Clear
                </button>
                <button
                  onClick={copySelected}
                  className={`px-3 py-1 rounded ${isDarkMode ? "bg-[#008300] text-white hover:bg-green-800" : "bg-[#008300] text-white hover:bg-green-700"}`}
                >
                  Copy URLs
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[60vh] overflow-auto pr-1">
              {endpoints.map((p) => (
                <label
                  key={p}
                  className={`flex items-center gap-3 p-2 rounded border ${isDarkMode ? "border-gray-700 hover:bg-gray-700" : "border-gray-200 hover:bg-gray-50"}`}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[p]}
                    onChange={() => handleToggle(p)}
                  />
                  <span
                    className={isDarkMode ? "text-gray-200" : "text-gray-700"}
                  >
                    {p}
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(selected)
                .filter(([, v]) => v)
                .slice(0, 6)
                .map(([p]) => (
                  <span
                    key={p}
                    className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? "bg-[#008300] text-green-200" : "bg-green-100 text-[#008300]"}`}
                  >
                    {p}
                  </span>
                ))}
              {Object.values(selected).filter(Boolean).length > 6 && (
                <span
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                >
                  +{Object.values(selected).filter(Boolean).length - 6} more
                </span>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={startSeoScan}
                disabled={
                  loading ||
                  Object.values(selected).filter(Boolean).length === 0
                }
                className={`px-6 py-3 rounded-lg font-semibold ${
                  loading ||
                  Object.values(selected).filter(Boolean).length === 0
                    ? "opacity-50 cursor-not-allowed bg-gray-400 text-gray-200"
                    : isDarkMode
                      ? "bg-green-700 text-white hover:bg-green-800"
                      : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                🚀 Start SEO Analysis (
                {Object.values(selected).filter(Boolean).length} selected)
              </button>
              <button
                onClick={copySelected}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? "bg-gray-700 text-gray-200 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
              >
                📋 Copy URLs
              </button>
              <a
                href={origin || "#"}
                target="_blank"
                rel="noreferrer"
                className={`px-4 py-2 rounded-lg ${isDarkMode ? "bg-gray-700 text-gray-200 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
              >
                🌐 Open Site
              </a>
            </div>
          </div>
        )}

        {!endpoints.length && !loading && (
          <div
            className={`rounded-xl shadow p-6 text-center ${isDarkMode ? "bg-gray-800 text-gray-300" : "bg-white text-gray-600"}`}
          >
            <div className="text-5xl mb-3">🔍</div>
            <p className="text-lg font-semibold mb-2">
              No endpoints discovered yet
            </p>
            <p className="text-sm">
              Enter a URL above and click "Find Endpoints" to discover pages on
              the website.
            </p>
          </div>
        )}
      </div>
    </div></>
  );
};

export default EndpointScanner;
